import type { ReactNode } from 'react'
import type { UserFilters } from '~/pages/backoffice/admin/user/list/use-user-filters'
import { RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { tourAnchor } from '~/helpers/tour/anchors'
import { parseProgress } from '~/pages/backoffice/admin/user/list/use-user-filters'
import { USER_PROGRESS_KEYS } from '~/pages/backoffice/admin/user/user-progress'
import { FilterSelect, SearchInput, TableToolbar, ToolbarActions } from '~/pages/backoffice/TableToolbar'

type IProps = {
  filters: UserFilters
  groups: readonly { id: string, name: string }[]
  onRefresh: () => void
  /** Les actions de la barre (ajouter, importer, exporter), selon les droits. */
  actions: ReactNode
}

/** La barre de la liste des comptes : recherche, filtres groupe (#135) et avancement (#137), actions. */
function UsersToolbar({ filters, groups, onRefresh, actions }: IProps) {
  const { t } = useTranslation()

  return (
    <TableToolbar>
      <SearchInput
        {...tourAnchor('users.search')}
        value={filters.search}
        onChange={e => filters.setSearch(e.target.value)}
      />
      <FilterSelect
        value={filters.groupId}
        onChange={e => filters.setGroupId(e.target.value)}
        aria-label={t('common.groups')}
        {...tourAnchor('users.filters')}
      >
        <option value="">{t('groups.all')}</option>
        {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
      </FilterSelect>
      <FilterSelect
        value={filters.progress}
        onChange={e => filters.setProgress(parseProgress(e.target.value))}
        aria-label={t('user.progress.label')}
      >
        <option value="">{t('user.progress.all')}</option>
        {Object.entries(USER_PROGRESS_KEYS).map(([value, key]) => (
          <option key={value} value={value}>{t(key)}</option>
        ))}
      </FilterSelect>
      <Button variant="outline" onClick={onRefresh} aria-label={t('common.refresh')}>
        <RefreshCw className="size-4" />
      </Button>
      <ToolbarActions>{actions}</ToolbarActions>
    </TableToolbar>
  )
}

export default UsersToolbar
